import PropTypes from 'prop-types';
import React from 'react';
import KTModal from '../../../components/Drawer';
import { getMetric } from '../config/metricGlossary';

/**
 * "Explain this metric" side drawer (tier 2 of the metric explanation system).
 * Renders a full glossary entry: definition, formula, how-to-read, gotchas,
 * and links to related metrics. Backed by config/metricGlossary.js so the same
 * copy powers tooltips and this drawer.
 */
const Section = ({ label, children }) => (
  <div className="mt-5">
    <div className="text-xs font-semibold uppercase text-grey-darker">
      {label}
    </div>
    <div className="mt-1 text-sm leading-relaxed text-black">{children}</div>
  </div>
);

Section.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const MetricDrawer = ({ metricKey, isOpen, onClose, onSelectMetric }) => {
  const metric = metricKey ? getMetric(metricKey) : null;

  return (
    <KTModal
      isOpen={isOpen}
      onClose={onClose}
      anchor="right"
      className="w-full max-w-md"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between border-b border-grey p-5">
          <div>
            <div className="text-xs font-medium uppercase text-primary">
              Metric explained
            </div>
            <h3 className="mt-1 text-lg font-semibold text-black">
              {metric ? metric.label : 'Metric'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-grey-darker hover:bg-grey-light"
          >
            <i className="sc-clear" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!metric ? (
            <p className="text-sm text-grey-darker">
              No explanation available.
            </p>
          ) : (
            <>
              <p className="text-sm leading-relaxed text-black">
                {metric.definition}
              </p>

              {metric.formula && (
                <Section label="Formula">
                  <code
                    className="block rounded-md bg-grey-light px-3 py-2 text-[13px] text-primary-dark"
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
                    }}
                  >
                    {metric.formula}
                  </code>
                </Section>
              )}

              {metric.howToRead && (
                <Section label="How to read it">{metric.howToRead}</Section>
              )}

              {metric.gotchas && metric.gotchas.length > 0 && (
                <Section label="Watch out for">
                  <ul className="list-disc space-y-1 pl-4">
                    {metric.gotchas.map(g => (
                      <li key={g}>{g}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {metric.related && metric.related.length > 0 && (
                <Section label="Related metrics">
                  <div className="flex flex-wrap gap-2">
                    {metric.related.map(key => {
                      const rel = getMetric(key);
                      if (!rel) return null;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => onSelectMetric && onSelectMetric(key)}
                          className="rounded-full bg-primary-lightest px-3 py-1 text-xs font-medium text-primary-dark hover:bg-primary-lighter"
                        >
                          {rel.label}
                        </button>
                      );
                    })}
                  </div>
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </KTModal>
  );
};

MetricDrawer.propTypes = {
  metricKey: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelectMetric: PropTypes.func,
};

export default MetricDrawer;
